import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getLegacyReferralOptions } from '@generated';
import { Calendar, MapPin, User } from 'lucide-react';
import { Button } from '~/shared/components/buttons/Button';
import { Modal } from '~/shared/components/modal/Modal';
import { SuspenseWrapper } from '~/shared/SuspenseWrapper';
import { LegacyReferralProvider } from './LegacyReferralContext';
import { StatusPill } from '~/shared/StatusPill';
import { formatAddress, getDisciplineAbbreviations, getReferralStatusIntent, getReferralStatusLabel } from './referralHelpers';

interface ReferralDetailsModalProps {
	referralId: string;
	onClose: () => void;
}

export function ReferralDetailsModal({ referralId, onClose }: ReferralDetailsModalProps) {
	return (
		<Modal title="Referral Details" closeModal={onClose}>
			<SuspenseWrapper fullScreen={false} name="referralDetailsModal">
				<ReferralDetailsContent referralId={referralId} onClose={onClose} />
			</SuspenseWrapper>
		</Modal>
	);
}

function ReferralDetailsContent({ referralId, onClose }: { referralId: string; onClose: () => void }) {
	const { data: referral } = useSuspenseQuery(getLegacyReferralOptions(referralId));

	const patientName = [referral.patientFirstName, referral.patientLastName].filter(Boolean).join(' ') || 'Unknown';
	const address = formatAddress(referral.address);

	const neededDisciplines = getDisciplineAbbreviations(referral.disciplinesNeeded);

	return (
		<LegacyReferralProvider referral={referral}>
			<div className="space-y-4">
				<div className="flex items-center gap-2">
					<User size={16} className="text-gray-400" />
					<h3 className="text-base font-semibold text-gray-900">{patientName}</h3>
					<span className="text-sm text-gray-500">{referral.mrNumber}</span>
					{referral.existingPatient && (
						<span className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
							Existing Patient
						</span>
					)}
				</div>

				<div className="flex items-center gap-1 text-sm text-gray-600">
					<MapPin size={16} className="text-gray-400" />
					<span>{address}</span>
				</div>

				<div className="flex items-center gap-4">
					<div className="flex items-center gap-1 text-sm text-gray-600">
						<Calendar size={16} className="text-gray-400" />
						<span className="font-medium">Start of Care:</span>
						<span>
							{new Date(referral.expectedStartOfCareDate).toLocaleDateString('en-US', {
								month: 'short',
								day: 'numeric',
								year: 'numeric'
							})}
						</span>
					</div>
					<StatusPill intent={getReferralStatusIntent(referral.statusId)}>{getReferralStatusLabel(referral.statusId)}</StatusPill>
				</div>

				<div className="flex items-center gap-2">
					<span className="text-sm font-medium text-gray-600">Disciplines Needed:</span>
					{neededDisciplines.map(d => (
						<span key={d} className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
							{d}
						</span>
					))}
				</div>

				{referral.primaryDiagnoses && (
					<div className="text-sm text-gray-600">
						<span className="font-medium">Primary Diagnosis:</span> {referral.primaryDiagnoses}
					</div>
				)}

				{referral.referralNotes && (
					<div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
						<span className="font-medium">Notes:</span> {referral.referralNotes}
					</div>
				)}

				<div className="flex justify-end">
					<Button onClick={onClose} appearance="outline" intent="neutral" label="Close" />
				</div>
			</div>
		</LegacyReferralProvider>
	);
}
